<template>
    <el-config-provider :locale="elementPlusLocale">
        <div id="app" class="h-full w-full">
            <transition name="fade">
                <div
                    data-testid="app-loading-overlay"
                    class="z-10 absolute h-screen w-screen flex items-center justify-center"
                    key="spinner"
                    v-if="(showLoading || contextLoading) && !maintenanceMode && !authUserSuspended"
                >
                    <activix-spinner :message="$t('general.loading')" />
                </div>
            </transition>

            <transition appear name="fade">
                <maintenance-mode v-if="maintenanceMode" />
                <suspended-access v-else-if="authUserSuspended" />
                <component
                    :is="layout"
                    :context-loading="contextLoading"
                    v-model:show-loading="showLoading"
                    v-else
                />
            </transition>

            <dynamic-modal-container />

            <!-- Generic portals -->
            <div id="portal-fullscreen-modal" />
            <div id="portal-modal-0" />
            <div id="portal-modal-1" />
            <div id="portal-modal-2" />
            <div id="portal-modal-3" />
            <div id="portal-modal-4" />

            <!-- Specific portals -->
            <div id="portal-modal-event" />
            <div id="portal-modal-event-sub" />
            <div id="portal-modal-select-picker" v-if="lgLayout" />

            <!-- Notifications -->
            <notifications />

            <!-- Screen Pop -->
            <screen-pop-container />
        </div>
    </el-config-provider>
</template>

<script lang="ts">
    import { useContextStore } from '@/store/modules/context/store.ts';
    import { useAccountStore } from '@/store/modules/account/store.js';
    import router from '@/router/index.ts';
    import AuthErrors from '@/constants/AuthErrors.ts';
    import { observeAssetRecoveryShellReady } from '@/telemetry/assetRecovery.ts';
    import { measureStartupPhase, observeStartupShell } from '@/telemetry/startupTiming.ts';
    import type { AccountEntity, AuthUserEntity } from '@/entities/types.ts';
    import { ElConfigProvider } from 'element-plus';
    import enLocale from 'element-plus/dist/locale/en.mjs';
    import frLocale from 'element-plus/dist/locale/fr.mjs';
    import { mapActions, mapState } from 'pinia';
    import { useEventBus } from '@/hooks/useEventBus.ts';
    import { useUserStore } from '@/store/modules/user/store.js';
    import requestErrorReporter, {
        removeBatchFromQueue,
        type RequestErrorEntry,
    } from '@/telemetry/requestErrorReporter.ts';
    import {
        buildBehaviorAuthUser,
        buildNewRelicAuthUser,
        buildSentryAuthUser,
    } from '@/telemetry/authUserPayloads.ts';
    import moment from './plugins/moment.js';

    import AuthLayout from './layouts/Auth.vue';
    import LandingLayout from './layouts/Landing.vue';
    import DefaultLayout from './layouts/Default.vue';
    import SplashscreenLayout from './layouts/Splashscreen.vue';

    import MaintenanceMode from './components/MaintenanceMode.vue';
    import Notifications from './components/Notifications.vue';
    import SuspendedAccess from './components/SuspendedAccess.vue';
    import ScreenPopContainer from './components/ScreenPopContainer.vue';
    import DynamicModalContainer from './components/DynamicModalContainer.vue';

    import { useGlobalStore } from './store/store.ts';

    const { on, off } = useEventBus();

    export default {
        name: 'App',
        compatConfig: { WATCH_ARRAY: false },
        components: {
            ElConfigProvider,
            AuthLayout,
            DefaultLayout,
            LandingLayout,
            MaintenanceMode,
            Notifications,
            SplashscreenLayout,
            SuspendedAccess,
            ScreenPopContainer,
            DynamicModalContainer,
        },

        data() {
            return {
                maintenanceMode: false,
                loaded: false,
                showLoading: true,
            };
        },

        computed: {
            ...mapState(useGlobalStore, ['errors']),

            assetRecoveryShellReady(): boolean {
                return (
                    this.loaded &&
                    !this.showLoading &&
                    !this.contextLoading &&
                    !this.maintenanceMode &&
                    !this.authUserSuspended
                );
            },

            elementPlusLocale() {
                return this.$i18n.getLocale() === 'fr-CA' ? frLocale : enLocale;
            },

            parentAuthUser(): AuthUserEntity | null {
                return useUserStore().parentAuthUser as AuthUserEntity | null;
            },

            authUser(): AuthUserEntity | null {
                return useUserStore().authUser as AuthUserEntity | null;
            },

            contextAccount(): AccountEntity | null {
                return useAccountStore().contextAccount as AccountEntity | null;
            },

            isLoggedIn(): boolean {
                return Boolean(this.$auth.check() && this.parentAuthUser?.id);
            },

            contextLoading(): boolean {
                if (
                    this.$route.meta.auth === false ||
                    this.$route.meta.landing ||
                    this.$route.meta.splashscreen
                ) {
                    return false;
                }

                return (
                    this.$wait.is([
                        'fetching.contextAccount',
                        'fetching.contextUsers',
                        'fetching.authUser',
                        'fetching.lead',
                    ]) ||
                    (!this.contextAccount?.id && this.$route.name !== 'accounts.store')
                );
            },

            authUserSuspended(): boolean {
                if (!this.isLoggedIn) {
                    return false;
                }

                return Boolean(this.parentAuthUser?.suspended || this.authUser?.suspended);
            },

            layout(): string {
                if (this.$route.meta.landing) {
                    return 'landing-layout';
                }

                if (this.$route.meta.splashscreen) {
                    return 'splashscreen-layout';
                }

                if (this.$route.meta.auth == false) {
                    return 'auth-layout';
                }

                return this.isLoggedIn ? 'default-layout' : 'auth-layout';
            },
        },

        watch: {
            assetRecoveryShellReady(ready: boolean) {
                observeStartupShell(ready, this.$route);
                if (ready) {
                    this.$nextTick(() => {
                        if (this.assetRecoveryShellReady) {
                            observeAssetRecoveryShellReady(this.$route);
                        }
                    });
                }
            },

            errors: {
                deep: true,
                handler(newValue) {
                    requestErrorReporter.onErrorsChanged(newValue.length);
                },
            },

            'parentAuthUser.id'(newUserId, oldUserId) {
                if (newUserId == oldUserId) {
                    return;
                }

                if (newUserId) {
                    const sentryAuthUser = buildSentryAuthUser(this.parentAuthUser);
                    const behaviorAuthUser = buildBehaviorAuthUser(this.parentAuthUser);
                    const newRelicAuthUser = buildNewRelicAuthUser(this.parentAuthUser);

                    this.$sentry.setAuthUser(sentryAuthUser);
                    this.$behavior.setAuthUser(behaviorAuthUser);
                    this.$newRelic.setAuthUser(newRelicAuthUser);

                    if (
                        this.$route.meta.landing !== true &&
                        this.$route.meta.splashscreen !== true &&
                        this.$route.meta.auth !== false
                    ) {
                        this.fetchConfigs();
                    }
                    this.setLocale();
                } else {
                    this.$sentry.clearAuthUser();
                    this.$newRelic.clearAuthUser();
                }

                this.$broadcasting.userChanged(newUserId, oldUserId);
            },

            'contextAccount.id'(newAccountId, oldAccountId) {
                if (newAccountId === oldAccountId) {
                    return;
                }

                this.$newRelic.setContextAccount(newAccountId);
                this.setLocale();
            },
        },

        methods: {
            ...mapActions(useGlobalStore, ['fetchConfigs', 'fetchFeaturePreviews']),

            setupRequestErrorReporter() {
                const globalStore = useGlobalStore();
                const errorStore = globalStore as unknown as {
                    errors: RequestErrorEntry[];
                };

                requestErrorReporter.configure({
                    store: {
                        acknowledgeBatch(batch: RequestErrorEntry[]) {
                            errorStore.errors = removeBatchFromQueue(errorStore.errors, batch);
                        },
                        getErrors() {
                            return errorStore.errors;
                        },
                    },
                });
            },

            /**
             * Managed request error reporter:
             * - one periodic scheduler,
             * - threshold-triggered immediate flush,
             * - transport abstraction (Sentry/NewRelic),
             * - retry/backoff for blocking transports.
             */
            startRequestErrorReporter() {
                requestErrorReporter.start();
            },

            setLocale(forcedLocale?: string) {
                const requestedLocale =
                    forcedLocale || this.parentAuthUser?.locale || this.$i18n.getLocale();
                const locale = this.$i18n.setLocale(requestedLocale);

                moment.locale(locale.toLowerCase());
                this.$axios.defaults.headers.common['X-Locale'] = locale;
                this.$axios.defaults.headers.common['Accept-Language'] = locale;
                localStorage.setItem('currentLocale', locale);
            },

            async init(requests: Promise<void>[] = []) {
                await Promise.all(requests);
                this.loaded = true;
            },

            async handleAuthInitializationFailure(error: unknown) {
                this.$sentry.captureException(error);
                this.loaded = true;
                this.showLoading = false;

                try {
                    await this.$router.replace({
                        name: 'auth.error',
                        query: {
                            error: AuthErrors.INITIALIZATION_FAILED,
                            target: this.$route.fullPath,
                        },
                    });
                } catch (navigationError) {
                    console.error('Failed to navigate to auth recovery:', navigationError);
                }
            },

            async fetchConfigsForInitialization() {
                try {
                    await measureStartupPhase('configs', () => this.fetchConfigs());
                } catch (error) {
                    this.$sentry.captureException(error);
                }
            },

            onSetLocale(locale: string) {
                const isPublicRoute =
                    this.$route.meta.landing === true ||
                    this.$route.meta.splashscreen === true ||
                    this.$route.meta.auth === false;
                if (!isPublicRoute && this.parentAuthUser?.id) {
                    this.parentAuthUser.locale = locale;
                    this.$api.users.update(this.parentAuthUser.id, {
                        locale,
                    });
                }

                this.setLocale(locale);
                if (!isPublicRoute) {
                    this.fetchConfigs();
                }
            },

            onSetMaintenanceMode(maintenanceMode: boolean) {
                this.maintenanceMode = maintenanceMode;
            },

            onLogout() {
                if (this.$auth.impersonating()) {
                    this.$auth.unimpersonate();
                } else {
                    this.$auth.logout();
                }
            },
        },

        async created() {
            const initialRoute = router.resolve(
                this.$auth.takeContextNavigationTarget() ??
                    `${globalThis.location.pathname}${globalThis.location.search}`
            );
            const isAuthCallback =
                initialRoute.name === 'auth.callback' || initialRoute.name === 'auth.mfaCallback';
            if (
                initialRoute.meta.landing === true ||
                initialRoute.meta.splashscreen === true ||
                (initialRoute.meta.auth === false && !isAuthCallback)
            ) {
                await this.init();
                if (initialRoute.meta.landing !== true) {
                    this.showLoading = false;
                }
                return;
            }
            try {
                if (
                    this.$auth.check() &&
                    !(await measureStartupPhase('admission', () =>
                        this.$auth.refreshFullCrmAccess()
                    ))
                ) {
                    await this.$router.replace({
                        name: 'forbidden',
                        query: { error: 'crmLiteFullCrmForbidden' },
                    });
                    await this.init();
                    this.showLoading = false;
                    return;
                }
                await measureStartupPhase('auth_accounts', () => this.$auth.fetch());
            } catch (error) {
                await this.handleAuthInitializationFailure(error);
                return;
            }

            await measureStartupPhase('configs_features', () =>
                this.init([
                    measureStartupPhase('features', () => this.fetchFeaturePreviews()),
                    this.fetchConfigsForInitialization(),
                ])
            );
            await measureStartupPhase('context', () =>
                useContextStore().setContextData(
                    initialRoute.path.startsWith('/auth/') ? null : initialRoute
                )
            );
            this.setLocale();
            this.setupRequestErrorReporter();
            this.startRequestErrorReporter();

            on('set-locale', this.onSetLocale);
            on('set-maintenance-mode', this.onSetMaintenanceMode);
            on('logout', this.onLogout);
        },

        beforeUnmount() {
            off('set-locale', this.onSetLocale);
            off('set-maintenance-mode', this.onSetMaintenanceMode);
            off('logout', this.onLogout);
            requestErrorReporter.stop();
            this.$openReplay.stop();
        },
    };
</script>
